import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";

import { inventories, mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { itemTypeObj } from "@/constants/items";
import { useModal } from "@/features/modal/ModalStackManager";
import { getLotId } from "@/features/pigment/form/MultiPigmentInputForm";
import customAlert from "@/features/ui/alert/Alert";
import { InventoriesAutoComplete } from "@/features/ui/autoComplete/inventoryLot/inventory-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { MantineSelectableTable } from "@/features/ui/table/MantineSelectableTable";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, NumberInput, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ProductionActionApiWorksWorkIdInputPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "mantine-react-table";
import { useEffect, useMemo } from "react";

const Create = Object.assign({}, FormMain, {
  Button: Button,
  TextInput: TextInput,
  Select: Select,

  Table: MantineSelectableTable,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  InventoriesAutoComplete: InventoriesAutoComplete,
  LocationAutoComplete: LocationAutoComplete,
  NumberInput: NumberInput,
});

type PageKeyType = "equipment" | "plan"; // pageKey의 가능한 값들을 정의합니다.

type ItemType<T extends PageKeyType> = T extends "equipment"
  ? AuthSignupPost201ResponseEquipmentWorksInner
  : ProductionPlansGet200ResponseRowsInnerWorksInner; // pageKey에 따른 item의 타입을 정의합니다.

interface WorkInputFormProps<T extends PageKeyType> {
  workRowData: ItemType<T>;
}

export function WorkInputForm<T extends PageKeyType>(params: WorkInputFormProps<T>): JSX.Element {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const actions = useWorkActions();

  const workData = params.workRowData;

  const targetLocationCode = (workData as ProductionPlansGet200ResponseRowsInnerWorksInner)
    ?.locationSetting?.toLocation?.code;

  const bomItemCodes = workData?.routingData?.routingBoms?.map((value) => value.itemCode);

  const routingBoms = workData?.routingData?.routingBoms;
  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemType: bom.itemWithGrade?.itemType,
    itemName: bom.itemWithGrade?.name,
    gradeName: bom.itemWithGrade?.gradeName,
    itemCategoryName: bom.itemWithGrade?.itemCategoryName,
    abstractItemCode: bom.itemWithGrade?.abstractItemCode,
    itemCode: bom.itemCode,
    quantityNumerator: bom.quantityNumerator,
  }));

  const form = useForm({
    initialValues: {
      quantityNumerator: 0,
      predictTotalQuantity: 0,
      predictQuantity: 0,
      useBarcode: false,
      barcodeInput: "",
      itemCode: "",
      workId: workData?.id,
      worksWorkIdInputPutRequest: {
        lotId: "",
        sourceLocationCode: "",
        targetLocationCode: targetLocationCode,
        quantity: 0,
      },
    },
  });

  const { mutate: workInputMutate } = useMutation(
    (params: ProductionActionApiWorksWorkIdInputPutRequest) =>
      mutateInventories
        .workInput(params)
        .mutationFn(params as ProductionActionApiWorksWorkIdInputPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        actions.setWorks((prevWorks) => []);
        closeModal(form.values);
        customAlert("원부자재 투입에 성공하였습니다.", "원부자재 투입 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "원부자재 투입" }),
          "원부자재 투입 실패",
          "red",
        );
      },
    },
  );

  const { data: inventoriesData } = useQuery(
    inventories.find({
      query: {
        $and: [
          {
            itemCode: form.values.itemCode ?? bomItemCodes,
          },
          {
            locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
          },
          { quantity: { $gt: 0 } },
        ],
      },
    }),
  );

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "itemType",
        header: "품목군",
        Cell: (rows) => {
          return <div>{itemTypeObj[rows.row.original.itemType as keyof typeof itemTypeObj]}</div>;
        },
      },
      {
        accessorKey: "itemName",
        header: "품목명",
      },
      {
        accessorKey: "gradeName",
        header: "GRADE",
      },
      {
        accessorKey: "itemCategoryName",
        header: "COLOR",
      },
      {
        accessorKey: "abstractItemCode",
        header: "SHAPE",
      },
      {
        accessorKey: "quantityNumerator",
        header: "비율" + "(%)",
      },
    ],
    [],
  );

  const handleOnSubmit = () => {
    const worksWorkIdInputPutRequest = {
      lotId: Number(getLotId(form.values.worksWorkIdInputPutRequest.lotId)),
      sourceLocationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
      targetLocationCode: form.values.worksWorkIdInputPutRequest.targetLocationCode,
      quantity: String(form.values.worksWorkIdInputPutRequest.quantity),
    };

    workInputMutate({
      workId: form.values.workId as number,
      worksWorkIdInputPutRequest: worksWorkIdInputPutRequest,
    });
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
    form.setFieldValue("quantityNumerator", selectedRows[0]?.quantityNumerator);
  };

  useEffect(() => {
    const itemCode = form.values.itemCode;
    const quantityNumerator = form.values.quantityNumerator;
    const predictTotalQuantity = form.values.predictTotalQuantity;

    if (itemCode && quantityNumerator && predictTotalQuantity) {
      const predictQuantity =
        Number(quantityNumerator) * Number(form.values.predictTotalQuantity) * 0.01;
      form.setFieldValue("predictQuantity", predictQuantity);
    }
  }, [form.values.itemCode, form.values.predictTotalQuantity]);

  return (
    <>
      <Create.Wrapper w={"100%"}>
        <Create.Table
          enableRowSelection={true}
          disabledCheckBox={!form.values.predictTotalQuantity}
          enableMultiRowSelection={false}
          columns={columns}
          data={convertedRoutingBoms ?? []}
          onRowSelection={handleOnRowSelection}
        />
        <Create.LocationAutoComplete
          withAsterisk
          label={"로케이션"}
          {...form.getInputProps("worksWorkIdInputPutRequest.sourceLocationCode")}
        />
        <Create.InventoriesAutoComplete
          disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
          withAsterisk
          label={"로트 정보"}
          value={form.values.worksWorkIdInputPutRequest.lotId}
          query={
            form.values.itemCode
              ? {
                  itemCode: form.values.itemCode,
                  locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
              : {
                  itemCode: bomItemCodes,
                  locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
          }
          onChange={(value) => {
            const quantity = Number(
              inventoriesData?.data?.filter((row) => row.lotId === Number(value))[0]?.quantity ?? 0,
            );
            form.setFieldValue("useBarcode", false);
            form.setFieldValue("worksWorkIdInputPutRequest.quantity", quantity);
            form.setFieldValue("worksWorkIdInputPutRequest.lotId", value ?? "");
          }}
        />
        <Create.TextInput
          withAsterisk
          disabled
          label={"입고 로케이션 코드"}
          value={targetLocationCode}
        />

        <Create.NumberInput
          withAsterisk
          label={"예상 생산총량"}
          {...form.getInputProps("predictTotalQuantity")}
        />
        <Flex justify={"space-between"}>
          <Create.NumberInput
            disabled
            label={"필요 원자재량"}
            value={form.values.predictQuantity}
          />
          <Create.NumberInput
            withAsterisk
            label={"투입량"}
            {...form.getInputProps("worksWorkIdInputPutRequest.quantity")}
          />
        </Flex>

        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            취소
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "inputMaterial",
              values: form.values.worksWorkIdInputPutRequest,
            })}
            onClick={handleOnSubmit}
          >
            원부자재 투입
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
}
